import { useEffect, useRef, useState } from "react";
import { FolderOpen, Plus, Search, Share2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ConfirmDialog from "@/components/ConfirmDialog";
import { RESOURCE_ICONS, isMediaResource, openTeachingResource, ResourcePlayerDialog } from "@/pages/enseignement/teachUtils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const TYPE_FILTERS = ["all", "pdf", "score", "image", "audio", "video", "document", "link"];

// Bibliothèque pédagogique de l'enseignant (Phase 3) : fichiers privés, partage par référence
export default function RessourcesPage() {
  const { t } = useAuth();
  const [resources, setResources] = useState([]);
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", external_url: "" });
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [shareRes, setShareRes] = useState(null);
  const [shareSel, setShareSel] = useState({ students: [], classes: [] });
  const [delRes, setDelRes] = useState(null);
  const [player, setPlayer] = useState(null);
  const fileRef = useRef(null);
  // Audio/vidéo → lecteur intégré identique à l'espace Groupe ; autres → blob/lien
  const openRes = (r) => (isMediaResource(r) ? setPlayer(r) : openTeachingResource(r));

  const load = () => {
    api.get("/teaching/resources").then((r) => setResources(r.data.resources)).catch(() => {});
    api.get("/teaching/students").then((r) => setStudents(r.data.students.filter((s) => s.status === "ACTIVE"))).catch(() => {});
    api.get("/teaching/classes").then((r) => setClasses(r.data.classes)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("title", form.title);
      fd.append("description", form.description);
      if (form.external_url.trim()) fd.append("external_url", form.external_url.trim());
      else if (file) fd.append("file", file);
      await api.post("/teaching/resources", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("teach.saved"));
      setAddOpen(false);
      setForm({ title: "", description: "", external_url: "" });
      setFile(null);
      load();
    } catch (e) { toast.error(e.friendly); }
    setUploading(false);
  };

  const openShare = (r) => {
    setShareRes(r);
    setShareSel({ students: r.student_relation_ids || [], classes: r.class_ids || [] });
  };

  const toggleShare = (kind, id) => {
    setShareSel((s) => ({
      ...s,
      [kind]: s[kind].includes(id) ? s[kind].filter((x) => x !== id) : [...s[kind], id],
    }));
  };

  const saveShare = async () => {
    try {
      await api.put(`/teaching/resources/${shareRes.id}/shares`, {
        student_relation_ids: shareSel.students,
        class_ids: shareSel.classes,
      });
      toast.success(t("teach.saved"));
      setShareRes(null);
      load();
    } catch (e) { toast.error(e.friendly); }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/teaching/resources/${delRes.id}`);
      setDelRes(null);
      load();
    } catch (e) { toast.error(e.friendly); }
  };

  const q = search.trim().toLowerCase();
  const filtered = resources.filter((r) =>
    (typeFilter === "all" || r.type === typeFilter) &&
    (!q || r.title.toLowerCase().includes(q))
  );

  return (
    <div className="space-y-4" data-testid="teach-resources-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="font-heading text-2xl font-bold flex items-center gap-2">
          <FolderOpen className="h-6 w-6 brand" /> {t("teach.resources_title")}
        </h2>
        <Button className="brand-bg text-white" onClick={() => setAddOpen(true)} data-testid="add-resource-button">
          <Plus className="h-4 w-4 mr-1" /> {t("teach.add_resource")}
        </Button>
      </div>

      <div className="flex gap-2 flex-wrap items-center">
        <div className="relative max-w-xs flex-1 min-w-48">
          <Search className="h-4 w-4 t-secondary absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("common.search")} className="pl-9 bg-transparent border-app" data-testid="resource-search-input" />
        </div>
        {TYPE_FILTERS.map((f) => (
          <button
            key={f}
            onClick={() => setTypeFilter(f)}
            className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${typeFilter === f ? "brand-bg text-white border-transparent" : "border-app t-secondary hover:bg-white/5"}`}
            data-testid={`resource-filter-${f}`}
          >
            {f === "all" ? t("agd.filter_all") : t(`teach.type_${f}`)}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <p className="text-sm t-secondary py-8 text-center" data-testid="resources-empty">{t("teach.no_resources")}</p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="resources-list">
          {filtered.map((r) => {
            const Icon = RESOURCE_ICONS[r.type] || RESOURCE_ICONS.document;
            const shareCount = (r.student_relation_ids?.length || 0) + (r.class_ids?.length || 0);
            return (
              <div key={r.id} className="app-card p-4 space-y-2" data-testid={`resource-card-${r.id}`}>
                <div className="flex items-start gap-3">
                  <Icon className="h-5 w-5 brand shrink-0 mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold truncate">{r.title}</p>
                    <p className="text-xs t-secondary">{t(`teach.type_${r.type}`)}{r.file_name ? ` — ${r.file_name}` : ""}</p>
                  </div>
                </div>
                <p className="text-[10px] font-semibold" data-testid={`resource-share-state-${r.id}`}>
                  {shareCount > 0
                    ? <span className="text-emerald-400">{t("teach.resource_shared")} ({shareCount})</span>
                    : <span className="t-secondary">{t("teach.resource_not_shared")}</span>}
                </p>
                <div className="flex gap-1.5 pt-1">
                  <Button size="sm" variant="outline" className="border-app h-8 text-xs flex-1" onClick={() => openRes(r)} data-testid={`resource-open-${r.id}`}>
                    {t(r.type === "link" ? "teach.open_link" : isMediaResource(r) ? "mp.play" : "teach.download")}
                  </Button>
                  <Button size="sm" variant="outline" className="border-app h-8 w-8 p-0" onClick={() => openShare(r)} data-testid={`resource-share-${r.id}`} aria-label={t("teach.share")}>
                    <Share2 className="h-3.5 w-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-rose-400" onClick={() => setDelRes(r)} data-testid={`resource-delete-${r.id}`} aria-label={t("teach.delete_resource")}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="card-bg border-app" data-testid="add-resource-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{t("teach.add_resource")}</DialogTitle>
            <DialogDescription className="t-secondary">{t("teach.resource_or_link")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="t-secondary text-xs uppercase tracking-wider">{t("teach.resource_title")}</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1 bg-transparent border-app" data-testid="resource-title-input" autoFocus />
            </div>
            <div>
              <Label className="t-secondary text-xs uppercase tracking-wider">{t("teach.resource_file")}</Label>
              <Input ref={fileRef} type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} className="mt-1 bg-transparent border-app" data-testid="resource-file-input" />
            </div>
            <div>
              <Label className="t-secondary text-xs uppercase tracking-wider">{t("teach.resource_url")}</Label>
              <Input value={form.external_url} onChange={(e) => setForm({ ...form, external_url: e.target.value })} placeholder="https://…" className="mt-1 bg-transparent border-app" data-testid="resource-url-input" />
            </div>
            <Button className="w-full brand-bg text-white" onClick={create} disabled={!form.title.trim() || (!file && !form.external_url.trim()) || uploading} data-testid="create-resource-confirm">
              {uploading ? t("teach.uploading") : t("common.create")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!shareRes} onOpenChange={() => setShareRes(null)}>
        <DialogContent className="card-bg border-app" data-testid="share-resource-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{t("teach.share_title", { name: shareRes?.title || "" })}</DialogTitle>
            <DialogDescription className="t-secondary">{t("teach.share")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 max-h-80 overflow-y-auto">
            <div>
              <p className="text-xs uppercase tracking-wider t-secondary mb-2">{t("teach.share_students")}</p>
              <div className="space-y-1.5" data-testid="share-students-list">
                {students.map((s) => (
                  <label key={s.id} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" className="accent-emerald-500" checked={shareSel.students.includes(s.id)} onChange={() => toggleShare("students", s.id)} data-testid={`share-student-${s.id}`} />
                    {s.prenom} {s.nom}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider t-secondary mb-2">{t("teach.share_classes")}</p>
              <div className="space-y-1.5" data-testid="share-classes-list">
                {classes.map((c) => (
                  <label key={c.id} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" className="accent-emerald-500" checked={shareSel.classes.includes(c.id)} onChange={() => toggleShare("classes", c.id)} data-testid={`share-class-${c.id}`} />
                    {c.name}
                  </label>
                ))}
              </div>
            </div>
            <Button className="w-full brand-bg text-white" onClick={saveShare} data-testid="share-save-confirm">{t("common.save")}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!delRes}
        title={t("teach.delete_resource")}
        description={t("teach.delete_resource_desc")}
        onConfirm={doDelete}
        onClose={() => setDelRes(null)}
        testid="resource-delete-confirm"
      />
      {player && <ResourcePlayerDialog resource={player} onClose={() => setPlayer(null)} />}
    </div>
  );
}
